import { inject, Injectable, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { PCP_LOGGER } from '@pcp/logging';
import { TranslocoService } from '@jsverse/transloco';
import {
    clearStoredLang,
    DEFAULT_LANG,
    isSupportedLang,
    SUPPORTED_LANGS,
    SupportedLang,
    writeStoredLang,
} from './language-config';

/**
 * Changes the language of the running app and persists the choice.
 *
 * The startup language is decided separately, by `readStoredLang()` feeding `defaultLang` in
 * `TranslocoRootModule` - this service is only about changing it afterwards. There is no UI for that;
 * the entry point is the `PcpDevTools` console facade, which is registered for non-production builds
 * only.
 */
@Injectable({ providedIn: 'root' })
export class LanguageConfigService {
    private readonly transloco = inject(TranslocoService);
    private readonly log = inject(PCP_LOGGER).createScoped('i18n');

    /** The languages the apps ship catalogues for. */
    readonly available: readonly SupportedLang[] = SUPPORTED_LANGS;

    /**
     * The language Transloco is *actually* rendering.
     *
     * Derived from `langChanges$`, deliberately not from the stored value: the two can disagree - a
     * direct `setActiveLang` elsewhere, or a storage write that failed - and in that case the rendered
     * language is the truth. Typed as `string` rather than `SupportedLang` for the same reason; it
     * reports what is active, it does not assert that it is one of ours.
     */
    readonly current: Signal<string> = toSignal(this.transloco.langChanges$, {
        initialValue: this.transloco.getActiveLang(),
    });

    /**
     * Switches the running app and persists the choice, so a reload keeps it. Rejects anything that is
     * not a supported language - the caller is a console, where `set('fr')` is a plausible typo.
     *
     * @returns whether the language was applied.
     */
    setLanguage(lang: string): boolean {
        if (!isSupportedLang(lang)) {
            this.log.warn('ignored unsupported language', { requested: lang, supported: SUPPORTED_LANGS });
            return false;
        }

        writeStoredLang(lang);
        this.transloco.setActiveLang(lang);
        return true;
    }

    /** Drops the override: removes the stored key and returns to the default language. */
    reset(): void {
        clearStoredLang();
        this.transloco.setActiveLang(DEFAULT_LANG);
    }
}
